import { createContext, useContext, ReactNode, useMemo, useCallback } from "react";
import { UseFieldArrayReturn, useFormContext } from "react-hook-form";

import { IPropertyBuildingForm } from "@rf/property/module/components/property-form/util/schemas/building-form/building.schema";

import useBuildingsHelp from "./hooks/useBuildingsHelp";

// Tipos específicos para mayor claridad
type IBuildingWithUuid = IPropertyBuildingForm & { uuid: string; id: string };
type ITemplateBuilding = IBuildingWithUuid[];

interface IBuildingsContextType {
  addBuilding: () => Promise<boolean>;
  deleteBuilding: (index: number) => Promise<boolean>;
  duplicateBuilding: (index: number, times?: number) => Promise<boolean>;
  fields: Record<"id", string>[];
  append: UseFieldArrayReturn["append"];
  remove: UseFieldArrayReturn["remove"];
  update: UseFieldArrayReturn["update"];
  getUpBuildingFromTemplate: (template: ITemplateBuilding) => void;
}

const BuildingsContext = createContext<IBuildingsContextType | undefined>(undefined);

interface IBuildingsProviderProps {
  children: ReactNode;
}

export const MxBuildingsProvider = ({ children }: IBuildingsProviderProps) => {
  const formState = useFormContext() as any;
  const {
    addBuilding,
    deleteBuilding,
    duplicateBuilding,
    fields,
    append,
    remove,
    update,
    getUpBuildingFromTemplate
  } = useBuildingsHelp(formState);

  // Memoización de funciones para evitar re-renders
  const memoizedAddBuilding = useCallback(addBuilding, [addBuilding]);
  const memoizedDeleteBuilding = useCallback(deleteBuilding, [deleteBuilding]);
  const memoizedDuplicateBuilding = useCallback(duplicateBuilding, [
    duplicateBuilding
  ]);
  const memoizedGetUpBuildingFromTemplate = useCallback(getUpBuildingFromTemplate, [
    getUpBuildingFromTemplate
  ]);

  const contextValue = useMemo<IBuildingsContextType>(
    () => ({
      addBuilding: memoizedAddBuilding,
      deleteBuilding: memoizedDeleteBuilding,
      duplicateBuilding: memoizedDuplicateBuilding,
      fields,
      append,
      remove,
      update,
      getUpBuildingFromTemplate: memoizedGetUpBuildingFromTemplate
    }),
    [
      memoizedAddBuilding,
      memoizedDeleteBuilding,
      memoizedDuplicateBuilding,
      fields,
      append,
      remove,
      update,
      memoizedGetUpBuildingFromTemplate
    ]
  );

  return (
    <BuildingsContext.Provider value={contextValue}>
      {children}
    </BuildingsContext.Provider>
  );
};

/**
 * Hook para acceder al contexto de Buildings.
 * Debe ser usado dentro de un MxBuildingsProvider.
 *
 * @throws {Error} Si se usa fuera del provider
 * @returns {IBuildingsContextType} El contexto de buildings
 */
export const useBuildingsContext = (): IBuildingsContextType => {
  const context = useContext(BuildingsContext);

  if (context === undefined) {
    throw new Error("useBuildingsContext must be used within a MxBuildingsProvider");
  }

  return context;
};

// Alias para mantener compatibilidad
export const BuildingsProvider = MxBuildingsProvider;
